import { dispatcher } from 'dispatcher';
import { useDispatch } from 'react-redux';
import { identity, prop, pick } from 'ramda';
import { useDataFetch } from 'js/hooks/useDataFetch';
import { useAddTranslations } from 'js/hooks/i18n';
import { htcmdService } from 'js/services/htcmd.service';
import { useCallback, useEffect, useState } from 'react';
import { useGetCMSResourceURL } from 'js/hooks/useGetCMSResourceURL';
import { clearTranslationsAction } from 'js/hooks/i18n/store/actions';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { get } from 'js/utils/fetch.util';

import * as actions from '../store/actions';

export const useGetResponseHandler = () => {
    const dispatch = useDispatch();
    const addTranslations = useAddTranslations();

    return useCallback((data) => {
        dispatch(clearTranslationsAction());
        addTranslations({ translations: data.translations });

        return data;
    }, [dispatch, addTranslations]);
};

export const useAppInitialResources = () => {
    const getCMSResourceURL = useGetCMSResourceURL();
    const [{ isLoading, error, data: app }, refetch] = useDataFetch({
        fetch: get,
        initialData: null,
        shouldFetch: identity,
        onResponse: useGetResponseHandler()
    });
    const [isAppReducerUpdated, setIsAppReducerUpdated] = useState(false);
    const formFactor = useDeviceSelector(prop('formFactor'));
    const dispatch = useDispatch();

    const { lang, country } = useUserSelector(pick(['lang', 'country']));
    const requestStamp = useAppSelector(prop('requestStamp'));

    useEffect(() => {
        dispatch(actions.fetchApp({ app: window.Playtech.Variables }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isLoading || isAppReducerUpdated || !app) {
            return;
        }

        dispatch(actions.fetchApp({ app, areInitialResourcesLoaded: true }));
        dispatch(actions.toggleIsChangeLangInProgress(false));
        // Propagate configuration from React to Backbone. Remove once we will remove Backbone.
        dispatcher.dispatch('app:config', app);
        // Since isLoading is changed earlier than app reducer is updated we need following
        if (!isAppReducerUpdated) {
            setIsAppReducerUpdated(true);
        }
    }, [app, dispatch, isLoading, isAppReducerUpdated]);

    useEffect(() => {
        if (!lang || !formFactor) {
            return;
        }

        const resourceURL = getCMSResourceURL(`/initialResources/${lang}_${formFactor}`, { t: requestStamp });

        refetch(resourceURL);
    }, [lang, formFactor, refetch, requestStamp, getCMSResourceURL]);

    useEffect(() => {
        if (isLoading) {
            setIsAppReducerUpdated(false);
        }
    }, [isLoading]);

    // @TODO move this to AppPostInit
    useEffect(() => {
        if (!country) {
            return;
        }

        if ((htcmdService.isEnabled || htcmdService.isWrapperEnabled) && !isLoading) {
            htcmdService.portalReady({ detectedCountry: country });
        }
    }, [country, isLoading]);

    return [{ isLoading: !isAppReducerUpdated, error, app }, refetch];
};
